import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

if (!customElements.get('soffa-gift-promo')) {
  customElements.define('soffa-gift-promo', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.config = JSON.parse(this.querySelector('[data-gift-config]').textContent);
      this.dialog = this.querySelector('[data-gift-dialog]');
      this.form = this.querySelector('[data-gift-form]');
      this.status = this.querySelector('[data-gift-status]');
      this.submitButton = this.form.querySelector('[type=submit]');
      this.preferenceKey = 'soffa-gift-choice-v1';
      this.seenKey = 'soffa-gift-promo-v3';
      try { this.preference = JSON.parse(localStorage.getItem(this.preferenceKey)); } catch (_) {}
      this.restoreChoice();
      this.form.addEventListener('submit', event => this.choose(event));
      this.querySelector('[data-gift-close]').addEventListener('click', () => { this.pendingCheckout = false; this.dialog.close(); });
      this.dialog.addEventListener('cancel', () => { this.pendingCheckout = false; });
      this.querySelector('[data-gift-skip]').addEventListener('click', () => this.skip());
      this.querySelector('[data-gift-cancel]').addEventListener('click', () => this.cancel());
      this.dialog.addEventListener('close', () => { try { localStorage.setItem(this.seenKey, '1'); } catch (_) {} });
      document.addEventListener('click', event => {
        if (event.target.closest('[data-soffa-gift-open]')) { event.preventDefault(); this.open(); }
      });
      const changed = event => event.promise?.then(({ detail }) => {
        if (detail?.source !== 'soffa-gift-promo') this.scheduleSync();
      }).catch(() => {});
      document.addEventListener(StandardEvents.cartLinesUpdate, changed);
      document.addEventListener(StandardEvents.cartDiscountUpdate, changed);
      document.addEventListener('submit', async event => {
        if (event.submitter?.name !== 'checkout' || this.allowCheckout) return;
        event.preventDefault();
        this.pendingCheckout = true;
        try {
          await this.sync(true);
          if (this.cart && this.qualifies(this.cart) && !this.verifiedGift(this.cart)) this.open();
          else this.continueCheckout();
        } catch (_) { this.open(); this.status.textContent = this.config.messages.error; }
      }, true);
      window.addEventListener('pageshow', () => this.scheduleSync());
      this.scheduleSync();
      if (location.hash === '#sofa-gift-offer') this.open();
      setTimeout(() => {
        let seen = false;
        try { seen = !!localStorage.getItem(this.seenKey); } catch (_) {}
        if (!seen && !document.querySelector('dialog[open]')) this.open();
      }, 1500);
    }

    get root() { return this.config.root.endsWith('/') ? this.config.root : this.config.root + '/'; }
    isGift(item) { return item.properties?._soffa_offer === 'rug-gift'; }
    gifts(cart) { return cart.items.filter(item => this.isGift(item)); }
    qualifies(cart) {
      return cart.items.reduce((sum, item) => sum + (this.isGift(item) || Number(item.variant_id) === this.config.bundleId ? 0 : item.original_line_price), 0) >= this.config.threshold;
    }
    verifiedGift(cart) {
      const gifts = this.gifts(cart);
      return gifts.length === 1 && gifts[0].quantity === 1 && gifts[0].final_line_price === 0;
    }
    signature(cart) {
      return cart.items.map(item => [item.variant_id, item.quantity, item.properties?._soffa_offer || ''].join(':')).sort().join('|');
    }
    selectedVariant() {
      const model = this.form.querySelector('[name=gift_model]:checked')?.value;
      const size = this.form.querySelector('[name=gift_size]:checked')?.value;
      return this.config.rugs.find(rug => String(rug.id) === model)?.variants.find(variant => variant.size === size && variant.available);
    }
    restoreChoice() {
      if (!this.preference) return;
      this.form.querySelectorAll('[name=gift_model]').forEach(input => { input.checked = input.value === String(this.preference.model); });
      this.form.querySelectorAll('[name=gift_size]').forEach(input => { input.checked = input.value === this.preference.size; });
    }
    savePreference(value) {
      this.preference = value;
      try { value ? localStorage.setItem(this.preferenceKey, JSON.stringify(value)) : localStorage.removeItem(this.preferenceKey); } catch (_) {}
    }
    open() {
      this.restoreChoice();
      this.status.textContent = this.cart && !this.qualifies(this.cart) ? this.config.messages.below : this.config.messages.before;
      this.querySelector('[data-gift-skip]').textContent = this.pendingCheckout ? this.config.messages.without : this.config.messages.skip;
      if (!this.dialog.open) this.dialog.showModal();
    }
    async api(path, payload) {
      const response = await fetch(this.root + path, { method: payload ? 'POST' : 'GET', headers: { Accept: 'application/json', ...(payload ? { 'Content-Type': 'application/json' } : {}) }, ...(payload ? { body: JSON.stringify(payload) } : {}) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || this.config.messages.error);
      return data;
    }
    sectionIds() { return [...new Set([...document.querySelectorAll('cart-items-component')].map(el => el.dataset.sectionId).filter(Boolean))].slice(0, 5); }
    publishCart(cart) {
      document.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: Promise.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { sections: cart.sections, items: cart.items, source: 'soffa-gift-promo', didError: false } }) }));
    }
    async removeGifts(cart) {
      const updates = Object.fromEntries(this.gifts(cart).map(item => [item.key, 0]));
      if (Object.keys(updates).length) return this.api('cart/update.js', { updates, attributes: { _soffa_gift_receipt: '' }, sections: this.sectionIds(), sections_url: location.pathname });
      return cart;
    }
    scheduleSync() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.sync().catch(() => { this.status.textContent = this.config.messages.error; }), 350);
    }
    async sync(checkout = false) {
      if (this.syncing) { await this.syncing; return this.sync(checkout); }
      this.syncing = this.reconcile(checkout);
      try { return await this.syncing; } finally { this.syncing = null; }
    }
    async reconcile(checkout) {
      let cart = await this.api('cart.js');
      const otherCode = cart.discount_codes?.some(code => code.applicable && code.code.toUpperCase() !== this.config.code.toUpperCase());
      if (otherCode) this.savePreference(null);
      const variant = this.config.rugs.find(rug => String(rug.id) === String(this.preference?.model))?.variants.find(variant => variant.size === this.preference?.size && variant.available);
      if (!this.qualifies(cart) || otherCode || !this.preference) {
        if (this.gifts(cart).length) { cart = await this.removeGifts(cart); this.publishCart(cart); }
        this.cart = cart;
        return cart;
      }
      const gifts = this.gifts(cart);
      if (variant && gifts.length === 1 && gifts[0].variant_id === variant.id && this.verifiedGift(cart)) {
        let receipt;
        try { receipt = JSON.parse(cart.attributes?._soffa_gift_receipt || 'null'); } catch (_) {}
        if (receipt?.signature === this.signature(cart) && receipt.total === cart.items_subtotal_price) {
          this.cart = cart;
          return cart;
        }
      }
      if (!variant || gifts.length > 1 || (gifts[0] && (gifts[0].variant_id !== variant.id || gifts[0].quantity !== 1))) cart = await this.removeGifts(cart);
      if (!variant) { this.cart = cart; return cart; }
      if (!this.gifts(cart).length) {
        await this.api('cart/add.js', { items: [{ id: variant.id, quantity: 1, properties: { _soffa_offer: 'rug-gift' } }] });
      }
      cart = await this.api('cart/update.js', { discount: this.config.code });
      if (!this.verifiedGift(cart)) {
        cart = await this.removeGifts(cart);
        this.cart = cart;
        this.publishCart(cart);
        throw new Error(this.config.messages.error);
      }
      const attributes = { _soffa_gift_receipt: JSON.stringify({ signature: this.signature(cart), total: cart.items_subtotal_price }) };
      if (document.querySelector('[name="attributes[soffa_delivery_area]"]:checked')) attributes.soffa_delivery_area = 'inside_green_line';
      cart = await this.api('cart/update.js', { attributes, sections: this.sectionIds(), sections_url: location.pathname });
      this.cart = cart;
      this.publishCart(cart);
      this.status.textContent = this.config.messages.selected;
      return cart;
    }
    async choose(event) {
      event.preventDefault();
      if (!this.form.reportValidity() || !this.selectedVariant()) return;
      this.savePreference({ model: this.form.querySelector('[name=gift_model]:checked').value, size: this.form.querySelector('[name=gift_size]:checked').value });
      this.submitButton.disabled = true;
      this.status.textContent = this.config.messages.working;
      try {
        this.cart = await this.api('cart.js');
        if (!this.qualifies(this.cart)) {
          this.dialog.close();
          location.assign('/discount/' + encodeURIComponent(this.config.code) + '?redirect=' + encodeURIComponent(this.config.shopUrl));
          return;
        }
        await this.api('cart/update.js', { discount: this.config.code });
        await this.sync();
        if (!this.verifiedGift(this.cart)) throw new Error(this.config.messages.error);
        this.dialog.close();
        if (this.pendingCheckout) this.continueCheckout();
      } catch (_) { this.status.textContent = this.config.messages.error; }
      finally { this.submitButton.disabled = false; }
    }
    async cancel() {
      this.pendingCheckout = false;
      this.savePreference(null);
      this.form.querySelectorAll('input[type=radio]').forEach(input => { input.checked = false; });
      try { await this.sync(); this.dialog.close(); } catch (_) { this.status.textContent = this.config.messages.error; }
    }
    async skip() {
      if (!this.pendingCheckout) { this.dialog.close(); return; }
      this.savePreference(null);
      try {
        let cart = await this.api('cart.js');
        cart = await this.removeGifts(cart);
        cart = await this.api('cart/update.js', { discount: this.config.code, sections: this.sectionIds(), sections_url: location.pathname });
        this.cart = cart;
        this.publishCart(cart);
        this.continueCheckout();
      } catch (_) { this.status.textContent = this.config.messages.error; }
    }
    continueCheckout() {
      this.dialog.close();
      this.pendingCheckout = false;
      this.allowCheckout = true;
      const button = document.getElementById('checkout');
      if (button?.form) button.form.requestSubmit(button);
      setTimeout(() => { this.allowCheckout = false; }, 1000);
    }
  });
}
